<template>
	<div class="list-container column">
		<div class="title-container">
			<div class="title">Brandskydd</div>
			<div class="dash-select" :class="{ mobile: isMobile }">
				<v-select
					class="panel-select dashboard"
					:value="selectedInfo"
					:options="infos"
					:placeholder="'Välj checklista'"
					:allowEmpty="false"
					:internalSearch="true"
					:showLabels="false"
					:custom-label="selectLabel"
					@input="selectInfo"
					label="floorName"
					track-by="id"
				>
					<span slot="noResult">Inga resultat</span>
					<span
						slot="caret"
						slot-scope="{ toggle }"
						class="caret"
						@mousedown.prevent.stop="toggle()"
					><i class="fas fa-caret-down caret-inner"></i
					></span>
				</v-select>
			</div>
		</div>
		<div class="map-container" :style="isMobile ? 'overflow: visible' : ''">
			<div
				id="dropZone"
				class="box plan-box"
				@contextmenu.prevent
				v-touch:touchhold="openMenu"
				v-touch-options="{ touchHoldTolerance: 1000 }"
				v-touch:moved="(mobileMoved = true)"
				v-touch:end="(mobileMoved = false)"
				@dblclick="openMenu"
				@mousewheel="zoom"
				@mousemove="moveMap"
				@mousedown="startMove"
				@mouseup="stopMove"
				@mouseleave="stopMove"
				:style="
					`padding-top: ${imgRatio *
						100}%; transform: scale(${zoomLevel}) translate(${mapX}px, ${mapY}px);
          cursor: ${movingMap ? 'grabbing' : 'grab'}`
				"
			>
				<img
					style="pointer-events: none"
					:src="getRoundImage(this.selectedInfo.fileId)"
					alt=""
					class="image-container"
					@load="updateRatio"
					:draggable="false"
				/>
				<div
					v-for="(item, index) in itemList"
					:key="'item' + index"
					:id="'item' + index"
					class="item-container"
					:style="{
						bottom:
							getPosition(item)[1] * areaHeightRatio -
							15 * itemScale(item) +
							'px',
						left:
							getPosition(item)[0] * areaWidthRatio -
							15 * itemScale(item) +
							'px',
						width: getItemSize(item),
						height: getItemSize(item)
					}"
				>
					<div
						class="circle"
						:style="{
							background: getStatus(item),
							width: `calc(100% + ${12 * itemScale(item)}px)`,
							height: `calc(100% + ${12 * itemScale(item)}px)`,
							'margin-top': `${-6 * itemScale(item)}px`,
							'margin-left': `${-6 * itemScale(item)}px`
						}"
					></div>
					<div
						class="placed-item"
						:class="[getItemType(item), { mobile: isMobile }]"
						:style="{ transform: `rotateZ(${item.rotation}deg)` }"
						@click.stop="openMenu($event, index)"
					></div>
				</div>
				<MenuComponent
					v-model="showMenu"
					:menuX="menuX"
					:menuY="menuY"
					:item="activeItem"
					:showOk="showOk"
					:areaWidthRatio="areaWidthRatio"
					@animated="animating = false"
					@addFlaw="addFlaw"
					@noFlaw="noFlaw"
					@seeInfo="seeInfo"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import iconMixin from '@/mixins/iconMixin.js';
import { mapGetters, mapMutations } from 'vuex';
import MenuComponent from './MenuComponent';

export default {
	mixins: [iconMixin],
	components: {
		MenuComponent
	},
	props: {
		map: Object,
		infos: Array
	},
	data: () => ({
		minimumScale: 0.3,
		areaWidthRatio: 1,
		areaHeightRatio: 1,
		selectedInfo: {},
		selectedIndex: 0,
		imgRatio: 1,
		showMenu: false,
		menuX: 0,
		menuY: 0,
		animating: false,
		zoomLevel: 1,
		mapX: 0,
		mapY: 0,
		movingMap: false,
		movedMap: false,
		showOk: false,
		activeItem: {},
		activeIndex: 0,
		itemList: [],
		mobileMoved: false,
		roundData: null,
		groupItemClicked: false,
		groupItemIndex: null,
		groupEvent: null
	}),
	computed: {
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('fileStore', ['getRoundImage']),
		...mapGetters('roundHistoryStore', ['activeRound'])
	},
	watch: {
		selectedInfo() {
			this.updateItems();
			this.updateRatio();
			this.updateImg();
		}
	},
	methods: {
		...mapMutations('roundHistoryStore', ['SET_ACTIVEROUND']),
		updateItems() {
			let roundData = { ...this.activeRound };
			this.selectedInfo = this.infos[roundData.currentInfo];
			let list = [];
			let groups = [];
			this.selectedInfo.roundHistoryItems.forEach(item => {
				let added = false;
				list.forEach(match => {
					const matchScale = this.getRawScaleNoZero(match);
					const itemScale = this.getRawScaleNoZero(item);
					const biggestScale = matchScale > itemScale ? matchScale : itemScale;
					const groupDistance = 19 * biggestScale;
					
					if (
						item.positionX < match.positionX + groupDistance &&
            item.positionX > match.positionX - groupDistance &&
            item.positionY < match.positionY + groupDistance &&
            item.positionY > match.positionY - groupDistance
					) {
						added = true;
						const groupIndex = groups.findIndex(
							group => group.items[0].id === match.id
						);

						if (groupIndex !== -1) {
							item.positionX = groups[groupIndex].positionX;
							item.positionY = groups[groupIndex].positionY;
							groups[groupIndex].items.push(item);
						} else {
							const group = {
								positionX: match.positionX,
								positionY: match.positionY,
								items: [match, item],
								type: 'Grupp',
								rotation: 0,
								scale: (matchScale+ itemScale) / 2
							};
							groups.push(group);
						}
					}
				});

				if (!added) {
					list.push(item);
				}

				added = false;
			});
			groups.forEach(group => {
				const itemIndex = list.findIndex(item => item.id === group.items[0].id);
				list.splice(itemIndex, 1);
			});
			list = list.concat(groups);

			if (this.selectedInfo.flaws) {
				list = list.concat(this.selectedInfo.flaws);
			}

			this.itemList = list;
		},
		selectLabel({ buildingName, floorName }) {
			return `${buildingName} - ${floorName}`;
		},
		selectInfo(info) {
			this.selectedIndex = this.infos.findIndex(
				element => element.id === info.id
			);
			this.selectedInfo = this.infos[this.selectedIndex];
			let roundData = { ...this.activeRound };
			roundData.currentInfo = this.selectedIndex;
			this.SET_ACTIVEROUND(roundData);
			this.$emit('update');
		},
		updateRatio() {
			const dropzone = document.getElementById('dropZone');

			if (dropzone) {
				this.roundData = { ...this.activeRound };
				this.areaWidthRatio = dropzone.offsetWidth / 1000;
				this.areaHeightRatio = dropzone.offsetHeight / 1000;
				this.roundData.widthRatio = this.areaWidthRatio;
				this.roundData.heightRatio = this.areaHeightRatio;
				this.SET_ACTIVEROUND(this.roundData);
			}
		},
		getStatus(item) {
			if (item.type === 'Grupp') {
				let hasFlaw = null;
				let hasNoFlaw = null;
				let controlled = true;
				let hasIgnoredFlaw = false;

				item.items.forEach(item => {
					const length = item.flaws.length;

					if (item.continueWithoutAdding) hasIgnoredFlaw = true;

					if (!item.controlled) {
						controlled = false;
					}

					if (length > 0) {
						if (!item.flaws[length - 1].done && item.controlled) hasFlaw = true;
						else if (!item.flaws[length - 1].done && !item.controlled)
							hasFlaw = false;
					} else if (length === 0 && item.controlled) {
						hasNoFlaw = true;
					}
				});

				if (hasFlaw && hasNoFlaw && controlled || hasIgnoredFlaw) return '#ff7b0065';
				else if (hasFlaw && hasNoFlaw === null && controlled)
					return '#ff000065';
				else if (hasNoFlaw && hasFlaw === null && controlled)
					return '#00ff1565';
				else '#0000ff65';
			} else if (item.controlled) {
				const flaws = item.flawScale ? [item] : item.flaws;

				if (flaws.length > 0) {
					return !flaws[flaws.length - 1].done ? '#ff000065' : '#00ff1565';
				} else return '#00ff1565';
			} else if (item.flaws) {
				const length = item.flaws.length || 0;

				if (length > 0 && !item.flaws[length - 1].done) return '#ff7b0065';
			} else if (item.flawScale) return '#ff000065';

			return '#0000ff65';
		},
		getItemType(item) {
			if (item.type === 'Grupp') return 'group';

			if (!item.type && !item.inventoryType) return 'flaw';

			return this.getType(item.inventoryType);
		},
		itemScale(item) {
			let scale = 1;

			if (item.inventoryType || item.type) {
				scale = item.scale;
			}

			if (item.flawScale) {
				scale = item.flawScale;
			}

			return scale < this.minimumScale
				? 1 * this.areaWidthRatio
				: scale * this.areaWidthRatio;
		},
		getItemSize(item) {
			return `${Math.round(30 * this.itemScale(item))}px`;
		},
		openMenu(event, index) {
			if (event.type === 'dblclick' && this.showMenu) return;

			if (this.animating || this.movedMap || this.mobileMoved) {
				return;
			}

			this.activeItem = this.itemList[index];

			if (this.activeItem?.items && this.activeItem?.items.length > 1) {
				this.groupItemClicked = true;
				this.groupItemIndex = index;
				this.groupEvent = event;
			} else {
				this.groupItemClicked = false;
				this.groupItemIndex = null;
				this.groupEvent = null;
			}

			this.activeIndex = index;

			if (index !== undefined) {
				this.showOk = true;
			} else {
				this.showOk = false;
			}

			if (this.isMobile && index === undefined) {
				let touch = event.touches[0];
				this.menuX = touch.clientX - touch.target.offsetLeft - 4.75;
				this.menuY = touch.clientY - touch.target.offsetTop - 4.75;
			} else if (index === undefined) {
				this.menuX = event.offsetX - 20;
				this.menuY = event.offsetY - 20;
			} else {
				this.menuX = event.target.parentNode.offsetLeft;
				this.menuY = event.target.parentNode.offsetTop;
			}

			if (!this.showMenu) {
				this.animating = true;
			}

			this.showMenu = true;
		},
		hideMenu() {
			if (!this.animating || !this.isMobile) {
				this.showMenu = false;
				this.animating = true;
			}
		},
		addFlaw(item) {
			let position = null;

			if (this.activeItem) {
				let itemIndex = this.selectedInfo.roundHistoryItems.findIndex(
					protection => {
						return protection.id == item.id;
					}
				);

				if (itemIndex === -1) {
					itemIndex = this.selectedInfo.flaws.findIndex(flaw => {
						return flaw.id == item.id;
					});

					if (itemIndex !== -1)
						itemIndex = this.selectedInfo.roundHistoryItems.length + itemIndex;
				}

				this.activeIndex = itemIndex;
				this.activeItem = item;
			} else {
				this.activeIndex = -1;
				position = [
					this.menuX / this.areaWidthRatio,
					this.menuY / this.areaHeightRatio
				];
			}

			this.$emit('addFlaw', this.activeIndex, position);
		},
		noFlaw(item) {
			if (item) {
				const itemIndex = this.selectedInfo.roundHistoryItems.findIndex(
					protection => {
						return protection.id == item.id;
					}
				);
				this.selectedInfo.roundHistoryItems[itemIndex].controlled = new Date();
				this.selectedInfo.roundHistoryItems[itemIndex].userName = this.email;
			} else {
				item.controlled = new Date();
				item.userName = this.email;
			}

			this.showMenu = false;

			if (this.groupItemClicked) {
				this.showMenu = true;
				this.openMenu(this.groupEvent, this.groupItemIndex);
			}

			this.$emit('saveState');
		},
		flawAdded(item) {
			const controlledItem = this.itemList.find(x => x.id === item.id) ?? item;

			controlledItem.flaws = item.flaws;
			controlledItem.controlled = new Date();

			this.itemList = [...this.itemList, controlledItem];
			this.showMenu = false;

			this.$emit('saveState');
			this.updateItems();

			if (this.groupItemClicked) {
				this.showMenu = true;
				this.openMenu(this.groupEvent, this.groupItemIndex);
			}
		},
		zoom(event) {
			let offsetX = 0;
			let offsetY = 0;
			let pageWidth = document.getElementById('dropZone').offsetWidth / 2;
			let pageHeight = document.getElementById('dropZone').offsetHeight / 2;
			let ratio = 1000 / Math.abs(event.deltaY);
			let left = 0;
			let top = 0;

			if (event.srcElement.tagName === 'circle') {
				let menu = document.getElementById('menu');
				left = event.offsetX + menu.offsetLeft - 10;
				top = event.offsetY + menu.offsetTop - 10;
			} else if (
				event.target.className.includes('placed-item') ||
        event.target.className.includes('menu-button')
			) {
				let offsetTop = 0;
				let offsetLeft = 0;
				let element = event.target.parentElement;

				if (event.target.className.includes('flaw')) {
					offsetLeft = 15;
					offsetTop = -15;
				}

				if (event.target.className.includes('ok')) {
					offsetLeft = 15;
					offsetTop = 45;
				}

				left = element.offsetLeft + event.offsetX + offsetLeft;
				top = element.offsetTop + event.offsetY + offsetTop;
			} else {
				left = event.offsetX;
				top = event.offsetY;
			}

			this.zoomLevel -= event.deltaY * 0.001;
			offsetX = left + this.mapX - pageWidth;
			offsetX = event.deltaY > 0 ? offsetX : -offsetX;
			offsetY = top + this.mapY - pageHeight;
			offsetY = event.deltaY > 0 ? offsetY : -offsetY;

			if (this.zoomLevel < 1) {
				this.zoomLevel = 1;
				offsetX = 0;
				offsetY = 0;
			} else if (this.zoomLevel > 2) {
				this.zoomLevel = 2;
				offsetX = 0;
				offsetY = 0;
			}

			this.mapX += (offsetX / ratio) * (1 / this.zoomLevel);
			this.mapY += (offsetY / ratio) * (1 / this.zoomLevel);
		},
		startMove() {
			this.movingMap = true;
		},
		async stopMove() {
			this.movingMap = false;
			function flushQueue() {
				return new Promise(resolve => setTimeout(resolve, 0));
			}
			await flushQueue().then(() => {
				this.movedMap = false;
			});
		},
		checkMapBounds() {
			const element = document.getElementById('dropZone');
			const zoom = this.zoomLevel > 1 ? this.zoomLevel - (this.zoomLevel - 1) * 0.35 : this.zoomLevel < 1
				? this.zoomLevel + (1 - this.zoomLevel) * 0.27
				: 1;
			const width = (element.offsetWidth / zoom) * 0.75;
			const height = (element.offsetHeight / zoom) * 0.75;
			this.mapX =
        this.mapX > width ? width : this.mapX < -width ? -width : this.mapX;
			this.mapY =
        this.mapY > height ? width : this.mapY < -height ? -height : this.mapY;
		},
		moveMap(event) {
			if (this.movingMap) {
				this.movedMap = true;
				this.mapX += event.movementX;
				this.mapY += event.movementY;
				this.checkMapBounds();
			}
		},
		updateImg() {
			var img = new Image();
			img.onload = () => {
				this.imageHeight = img.height;
				this.imageWidth = img.width;
				this.imgRatio = this.imageHeight / this.imageWidth;
			};
			img.src = this.getRoundImage(this.selectedInfo.fileId);
		},
		seeInfo(item) {
			this.$emit('seeInfo', item);
		},
		getPosition(item) {
			if (item.positionX) {
				return [item.positionX, item.positionY];
			} else if (item.flawPositionX || item.flawPositionX === 0) {
				return [item.flawPositionX, item.flawPositionY];
			}
		},
		getRawScaleNoZero(item) {
			return item.scale < this.minimumScale ? 1 : item.scale;
		}
	},
	created() {
		window.addEventListener('resize', this.updateRatio);
		let roundData = { ...this.activeRound };
		this.selectedIndex = roundData.currentInfo;
		this.selectedInfo = this.infos[roundData.currentInfo];
		this.updateImg();
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.updateRatio);
	}
};
</script>

<style scoped lang="scss">
@import "@/assets/colors.scss";

$border-radius: 1px;

.title-container {
  display: flex;
  flex-wrap: wrap;
}
.dash-select {
  margin-top: -0.5rem;
  margin-left: auto;
  &.mobile {
    margin-right: 0.5rem;
    margin-left: auto;
    margin-top: -0.5rem;
    margin-bottom: 0.5rem;
  }
}

.map-container {
  width: 100%;
  overflow: hidden;
  height: calc(100% - 60px);
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Opera and Firefox */
}
.box.plan-box {
  width: 100%;
  padding: 0;
  background-size: contain;
  background-repeat: no-repeat;
  margin-left: auto;
  margin-right: auto;
  border-radius: $border-radius;
  box-shadow: none;
  cursor: grab;
}

.image-container {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Opera and Firefox */
}
.menu {
  z-index: 2;
  position: absolute;
  pointer-events: none;
  .close-button {
    cursor: pointer;
  }
  .menu-button {
    pointer-events: all;
  }
  .cross {
    stroke: black;
    pointer-events: none;
  }
  .svg-container {
    pointer-events: none;
    svg circle {
      pointer-events: all;
    }
  }
}
.item-container {
  position: absolute;
  .placed-item {
    position: absolute;
    top: 0;
    left: 0;
    background-color: transparent;
    cursor: pointer;
    background-size: contain;
    width: 100%;
    height: 100%;
  }
  .circle {
    position: absolute;
    width: calc(100% + 12px);
    height: calc(100% + 12px);
    margin-top: -6px;
    margin-left: -6px;
    background: #ff000065;
    border-radius: 100%;
    pointer-events: none;
  }
}

.flaw,
.no-flaw {
  &.menu-button {
    background-color: transparent;
    cursor: pointer;
    z-index: 2;
    position: absolute;
    background-size: contain;
  }
}
</style>

<style lang="scss">
.multiselect.panel-select.dashboard {
  min-width: 220px;
  max-width: 220px;
  .caret {
    background-color: #fcfcfc;
  }
  .multiselect__tags {
    background-color: white;

    .multiselect__single,
    .multiselect__input {
      background-color: white;
    }
  }
  .multiselect__content {
    background: white;
  }
}
</style>
